/**
 * Admin queue of engineering review requests for commissioning records.
 * Route: /admin/commissioning/reviews
 *
 * Lists pending → approved/rejected/needs_changes reviews. Each row shows the
 * compressor, signer, sequence, and a quick decide form (decision + notes).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuthenticatedDownload } from "@/lib/useAuthenticatedDownload";
import AccountMenu from "@/components/AccountMenu";
import { useT } from "@/lib/i18n";

const STATUS_TONES = {
  pending_review: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  needs_changes: "bg-orange-100 text-orange-800",
};

export default function CommissioningReviewsPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [decideFor, setDecideFor] = useState(null);
  const [decisionNote, setDecisionNote] = useState("");
  // iter150AZ.60 — authenticated PDF download (was an `<a target="_blank">`
  // that dropped X-User-Email and failed with 403).
  const { downloading: pdfDl, download: dlPdf } = useAuthenticatedDownload();

  const refresh = async () => {
    try {
      const r = await api.get("/commissioning/engineering/reviews",
                              { params: { status: filter, limit: 100 } });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Could not load reviews");
    }
  };
  useEffect(() => { refresh(); }, [filter]);

  const decide = async (decision) => {
    try {
      await api.post(`/commissioning/engineering/reviews/${decideFor.id}/decide`,
                     { decision, notes: decisionNote });
      toast.success(`Marked as ${decision}`);
      setDecideFor(null);
      setDecisionNote("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 px-4 md:px-8 h-14 flex items-center gap-3 max-w-6xl mx-auto">
        <button data-testid="reviews-back-home" onClick={() => navigate("/")}
          className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"
          title="Home">
          <ArrowLeft size={18} weight="bold" />
        </button>
        <ClipboardText size={20} weight="fill" className="text-orange-300" />
        <h1 className="text-base font-black tracking-tighter font-['Chivo']" data-testid="reviews-title">
          {t("eng_reviews.title", "Engineering Review Queue")}
        </h1>
        <div className="ml-auto"><AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} /></div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="mb-3 flex gap-2 flex-wrap">
          {["", "pending_review", "approved", "rejected", "needs_changes"].map(s => (
            <button key={s || "all"} onClick={() => setFilter(s)}
              data-testid={`reviews-filter-${s || "all"}`}
              className={`px-3 py-1 rounded text-sm border ${filter === s ? "bg-violet-600 text-white border-violet-600" : "bg-white"}`}>
              {s.replace("_", " ") || "all"}
            </button>
          ))}
        </div>
        <div className="bg-white border rounded-md divide-y" data-testid="reviews-list">
          {items.length === 0 && <div className="p-6 text-center text-sm text-slate-500">{t("eng_reviews.empty", "No reviews match.")}</div>}
          {items.map(it => (
            <div key={it.id} data-testid={`reviews-row-${it.id}`} className="p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-sm font-bold">SN {it.serial_number} · #{it.sequence_number}</div>
                  <div className="text-xs text-slate-600">{it.company_name}</div>
                  <div className="text-xs text-slate-400 mt-1">Sent {new Date(it.sent_at).toLocaleString()} by {it.sent_by}</div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${STATUS_TONES[it.status] || "bg-slate-100"}`}>
                  {(it.status || "").replace("_", " ")}
                </span>
              </div>
              {it.notes && <div className="mt-2 text-xs text-slate-700 whitespace-pre-wrap p-2 bg-slate-50 rounded">{it.notes}</div>}
              {it.review_decision && (
                <div className="mt-2 text-xs text-slate-600">
                  <span className="font-bold">Decision:</span> {it.review_decision} · by {it.reviewed_by} · {new Date(it.reviewed_at).toLocaleDateString()}
                  {it.review_notes && <div className="mt-1 italic">{it.review_notes}</div>}
                </div>
              )}
              <div className="mt-2 flex gap-2 flex-wrap">
                <button type="button"
                  data-testid={`reviews-view-pdf-${it.visit_id}`}
                  disabled={pdfDl}
                  onClick={() => dlPdf(
                    `/commissioning/${it.visit_id}/disclaimer.pdf`,
                    `commissioning_visit_${it.visit_id}_disclaimer.pdf`,
                  )}
                  className="text-xs text-blue-600 hover:text-blue-800 font-bold disabled:opacity-60 disabled:cursor-wait bg-transparent border-0 p-0">
                  {pdfDl ? "…" : t("eng_reviews.view_pdf", "View signed PDF →")}
                </button>
                <button onClick={() => navigate(`/commissioning/${it.visit_id}`, { state: { from: "/admin/commissioning/reviews" } })}
                  className="text-xs text-violet-600 hover:text-violet-800 font-bold">{t("eng_reviews.open_commissioning", "Open commissioning →")}</button>
                {it.status === "pending_review" && (
                  <button data-testid={`reviews-decide-${it.id}`} onClick={() => setDecideFor(it)}
                    className="text-xs text-emerald-700 hover:text-emerald-900 font-bold">Decide…</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
      {decideFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-md shadow-xl max-w-md w-full p-5">
            <h3 className="text-lg font-bold mb-2">{t("eng_reviews.decide_title", "Decide review")}</h3>
            <p className="text-sm text-slate-600 mb-3">SN {decideFor.serial_number} · #{decideFor.sequence_number}</p>
            <textarea data-testid="reviews-decision-note"
              value={decisionNote}
              onChange={e => setDecisionNote(e.target.value)}
              rows={4}
              className="w-full border rounded px-2 py-1 text-sm"
              placeholder={t("eng_reviews.notes_placeholder", "Notes (optional)…")} />
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setDecideFor(null)} className="px-3 py-2 text-sm border rounded">{t("action.cancel", "Cancel")}</button>
              <button data-testid="reviews-needs-changes" onClick={() => decide("needs_changes")}
                className="px-3 py-2 text-sm bg-orange-500 text-white rounded font-bold">{t("eng_reviews.needs_changes", "Needs changes")}</button>
              <button data-testid="reviews-reject" onClick={() => decide("rejected")}
                className="px-3 py-2 text-sm bg-red-600 text-white rounded font-bold">Reject</button>
              <button data-testid="reviews-approve" onClick={() => decide("approved")}
                className="px-3 py-2 text-sm bg-emerald-600 text-white rounded font-bold">Approve</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
